/**
 * The calendar tab's wiring: which range is shown, the reads behind it, the one gate that owns
 * their wait, and which day or session the right column is about.
 *
 * Decisions are `model/`'s — this hook reads, remembers what the trainer chose, and calls the entry
 * points. It returns one object, so the tab's components take a slice of it rather than a dozen
 * props.
 *
 * ── What the one gate waits for ──────────────────────────────────────────────────────────────
 * Everything the screen draws on ARRIVAL: the shown range's schedule and hours, the booking
 * policy, the week's free starts when clients book themselves, the decision window, and the
 * credit summary the session panel is sized from, and the Google connection and (on an Apple
 * device) the Apple Calendar feed the one-time card over the grid is decided from
 * (`useGoogleCalendarPrompt`) — their failure is no card, or the card without Apple. A section
 * waits for all of its reads, so the column's foot never pops in after the list above it. Only the
 * grid's two reads can FAIL the screen; the foot's reads fail quietly into a line of their own.
 *
 * After the first answer the foot's reads never gate the grid again: they keep their previous
 * answer while a new key is read (`keepPreviousData`), and the foot alone shows it is re-reading.
 * Choosing a day in another week, or the midnight tick moving the decision window, must not blank
 * the grid, remount it and drop the trainer's focus.
 */

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { browserTimeZone } from '~/shared/lib/occurrenceZone';
import { preferenceStore } from '~/shared/lib/preferenceStore';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { BOOKING_ROOT, bookingPolicyQueryOptions, creditSummaryQueryOptions } from '~/features/credit-ledger';
import type { TrainWithMeModulesTrainingDtosResponsesCalendarEventResponse as CalendarEventResponse } from '~/shared/api/generated/model';
import { calendarFeedQueryOptions } from '~/entities/calendar-feed';
import { googleCalendarQueryOptions } from '~/entities/google-calendar-connection';
import { standingCallLinkQueryOptions } from '~/entities/standing-call-link';
import { isApplePlatform } from '~/shared/lib/applePlatform';

import {
  trainerAvailabilityQueryOptions,
  trainerScheduleQueryOptions,
  trainerSlotsQueryOptions,
} from '../api/bookingQueries';
import { bookingCalendar, type BookingCalendar } from '../model/bookingCalendar';
import { readBookingSession, type BookingSession } from '../model/bookingSession';
import { backendOffersCalls, googleMeetSourceOf, type GoogleMeetSource } from '../model/callLink';
import { callLinkChoice, type CallLinkChoiceView } from '../model/callLinkChoice';
import {
  shownAnchor,
  stepAnchor,
  visibleRange,
  type CalendarView,
  type VisibleRange,
} from '../model/calendarRange';
import { dayAgenda, type DayAgenda } from '../model/dayAgenda';
import { readDayPlanShown, writeDayPlanShown } from '../model/dayPlanPreference';
import { draftPolicy, type DraftPolicy } from '../model/sessionDraft';
import {
  bookingZone,
  mondayOfDay,
  shiftDay,
  wallReading,
  zoneNotice,
  type CivilDay,
} from '../model/trainerClock';
import { awaitingDecision, firstFreeStarts, type FreeStart } from '../model/weekSideNotes';
import { useMinuteClock } from './useMinuteClock';

/**
 * How far back "Wymaga Twojej decyzji" looks, whatever week is on screen. A settled verdict is owed
 * about a session that ended; a month is where the mobile app's own rail stops reading.
 */
const DECISION_WINDOW_DAYS = 30;

interface ReadState {
  readonly data: unknown;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly isFetching: boolean;
}

/**
 * A read the screen is waiting on: its first answer, or the retry of a first answer that failed —
 * `isPending` alone never re-opens. A read that already HAS data is never waited on again: its
 * retry re-reads behind the week on screen rather than blanking it.
 */
function isWaiting(read: ReadState): boolean {
  return read.isPending || (read.isError && read.isFetching && read.data === undefined);
}

/** Failed with nothing to show — the first load. A failed REFRESH keeps the cached week. */
function failedOutright(read: ReadState): boolean {
  return read.isError && read.data === undefined;
}

/** The grid's answer once the gate has let go: it failed, or here it is. */
export type BookingContent =
  | { readonly kind: 'failed' }
  | { readonly kind: 'ready'; readonly calendar: BookingCalendar; readonly agenda: DayAgenda };

/** The column's foot: not shown, could not be read, or the week's first free starts. */
export type FreeStartsNote =
  | { readonly kind: 'hidden' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'ready'; readonly starts: readonly FreeStart[] };

/** How the grid changed last — which of the motion dictionary's calendar steps its swap plays. */
export type GridMove = 'calendarView' | 'calendarForward' | 'calendarBack';

export interface BookingCalendarState {
  readonly view: CalendarView;
  readonly anchor: CivilDay;
  readonly range: VisibleRange;
  readonly today: CivilDay;
  /** The zone the grid is drawn in — the trainer's, or the server's default where it has none. */
  readonly zone: string;
  /** The zone to NAME beside the range, or `null` when it is the reader's own (`zoneNotice`). */
  readonly zoneNotice: string | null;
  /** How many answers the shown range's schedule has given — what a written overlay is judged by. */
  readonly readAnswers: number;
  /** The lengths a new session starts from — the booking policy's, or the server's defaults. */
  readonly policy: DraftPolicy;
  readonly nowMs: number;
  readonly gate: SkeletonGate;
  /** `null` only while the grid's reads are still out. */
  readonly content: BookingContent | null;
  /**
   * A refresh of the week on screen failed, and the screen still shows what it last read. Said
   * quietly beside the title, never by blanking the grid.
   */
  readonly stale: boolean;
  readonly retry: () => void;
  readonly selectedDay: CivilDay;
  readonly awaiting: readonly BookingSession[];
  /** „Rozmowa wideo" may be offered on a new consultation (`model/callLink` `backendOffersCalls`). */
  readonly callsOffered: boolean;
  /** Whether Google will make a Meet for a new video consultation (`model/callLink`). */
  readonly googleMeet: GoogleMeetSource;
  /** The create panel's link choice (`model/callLinkChoice`), `null` while the connection is read. */
  readonly callChoice: CallLinkChoiceView | null;
  /** The trainer's standing call link, prefilled and sent as Standing when untouched. */
  readonly standingCallLink: string | null;
  readonly freeStarts: FreeStartsNote;
  /** The day before / after the chosen one, paging the grid when it leaves the range. */
  readonly stepDay: (direction: -1 | 1) => void;
  readonly retryFreeStarts: () => void;
  readonly openSession: BookingSession | null;
  readonly gridMove: GridMove;
  /** The viewer's remembered choice: the day's plan beside the grid, or the grid alone. */
  readonly planShown: boolean;
  /** Whether the column stands at all — the choice, or a session open in it. */
  readonly columnShown: boolean;
  readonly setPlanShown: (shown: boolean) => void;
  readonly setView: (view: CalendarView) => void;
  readonly goToday: () => void;
  readonly goPrevious: () => void;
  readonly goNext: () => void;
  readonly selectDay: (day: CivilDay) => void;
  readonly openSessionById: (id: number) => void;
  /** Open a session the grid may not have read yet — one just written — paging to its day. */
  readonly showSession: (id: number, day: CivilDay) => void;
  readonly closeSession: () => void;
}

export function useBookingCalendar(): BookingCalendarState {
  const nowMs = useMinuteClock();
  const [view, setViewState] = useState<CalendarView>('week');
  const [chosenAnchor, setChosenAnchor] = useState<CivilDay | null>(null);
  const [chosenDay, setChosenDay] = useState<CivilDay | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [gridMove, setGridMove] = useState<GridMove>('calendarView');
  const [planShown, setPlanShownState] = useState(() => readDayPlanShown(preferenceStore()));
  const queryClient = useQueryClient();

  // The zone arrives WITH the hours; until it does, the server's default stands in for it — the
  // same clock the server itself would use for a trainer it cannot place.
  const [publishedZone, setPublishedZone] = useState<string | null>(null);
  const zone = bookingZone(publishedZone);
  const today = wallReading(nowMs, zone).day;
  const anchor = shownAnchor(chosenAnchor, nowMs, zone);

  const range = visibleRange(view, anchor);
  const schedule = useQuery(trainerScheduleQueryOptions(range));
  const availability = useQuery(trainerAvailabilityQueryOptions(range));
  const policy = useQuery(bookingPolicyQueryOptions());
  const decisions = useQuery({
    ...trainerScheduleQueryOptions({ first: shiftDay(today, -DECISION_WINDOW_DAYS), last: today }),
    placeholderData: keepPreviousData,
  });

  const readZone = availability.data?.trainerTimeZoneId ?? null;
  if (availability.data !== undefined && readZone !== publishedZone) setPublishedZone(readZone);

  const selectedDay =
    chosenDay !== null && chosenDay >= range.first && chosenDay <= range.last
      ? chosenDay
      : today >= range.first && today <= range.last
        ? today
        : view === 'month' && anchor >= range.first && anchor <= range.last
          ? anchor
          : range.first;

  // "Wolne terminy … w tym tygodniu": the week of the chosen day, from today where today is in it.
  const monday = mondayOfDay(selectedDay);
  const slotsWeek = { first: monday < today ? today : monday, last: shiftDay(monday, 6) };
  const selfBooking = policy.data?.selfBookingEnabled === true;
  const slotsWanted = selfBooking && slotsWeek.first <= slotsWeek.last;
  const slots = useQuery({
    ...trainerSlotsQueryOptions(slotsWeek, slotsWanted),
    placeholderData: keepPreviousData,
  });

  // The foot's reads gate only their FIRST answer; afterwards a new key is the foot's own wait.
  const firstAnswer = (read: ReadState) => read.data === undefined && isWaiting(read);
  // The trainer's credit summary: one small read the session panel sizes itself from, so it has
  // answered before any session can be opened. Only its FIRST answer is waited for — a failure is
  // the panel's quiet line, and its retry (which the library reports as `pending` again) must not
  // blank the grid.
  const credits = useQuery(creditSummaryQueryOptions());
  const google = useQuery(googleCalendarQueryOptions());
  // Read only on an Apple device: a disabled query reports `pending` for ever.
  const appleDevice = isApplePlatform(globalThis.navigator);
  const feed = useQuery(calendarFeedQueryOptions(appleDevice));
  // Not part of the screen's gate: only the create panel's prefill waits for it.
  const standing = useQuery(standingCallLinkQueryOptions());
  const standingCallLink = standing.data?.link ?? null;
  const gate = useSkeletonGate(
    isWaiting(schedule) ||
      isWaiting(availability) ||
      firstAnswer(policy) ||
      firstAnswer(decisions) ||
      (slotsWanted && firstAnswer(slots)) ||
      (credits.isPending && credits.errorUpdateCount === 0) ||
      firstAnswer(google) ||
      (appleDevice && firstAnswer(feed)),
  );

  const calendar =
    schedule.data !== undefined && availability.data !== undefined
      ? bookingCalendar({
          events: schedule.data,
          availability: availability.data,
          range,
          zone,
          nowMs,
        })
      : null;

  const content: BookingContent | null =
    failedOutright(schedule) || failedOutright(availability)
      ? { kind: 'failed' }
      : calendar === null
        ? null
        : {
            kind: 'ready',
            calendar,
            agenda: dayAgenda({
              day: selectedDay,
              today,
              sessions: calendar.sessions,
              blocks: calendar.blocks,
              zone,
              nowMs,
            }),
          };

  const freeStarts: FreeStartsNote = policy.isError
    ? { kind: 'failed' }
    : !slotsWanted
      ? { kind: 'hidden' }
      : slots.isError
        ? { kind: 'failed' }
        : slots.data === undefined || slots.isPlaceholderData
          ? { kind: 'pending' }
          : {
              kind: 'ready',
              starts: firstFreeStarts(slots.data, slotsWeek.first, slotsWeek.last, nowMs),
            };

  const decisionSessions = (decisions.data ?? []).map((event) => readBookingSession(event, zone));
  // A session the column can open: one on the grid, or one the decision window names, wherever
  // the grid happens to be.
  const known = [...(calendar?.sessions ?? []), ...decisionSessions];
  const findSession = (id: number) => known.find((session) => session.id === id) ?? null;
  const found = openId === null ? null : findSession(openId);
  // The session last seen open, kept while a read that has not caught up with it stands on screen —
  // "Przełóż" to a week already cached pages the grid to data from BEFORE the move, which does not
  // hold the session yet. Without this the column fell back to the day's plan (or closed) for the
  // round trip. Adjusted during render on the session's identity, never on object identity.
  const [lastOpen, setLastOpen] = useState<BookingSession | null>(null);
  const seen = (session: BookingSession | null) =>
    session === null
      ? ''
      : `${String(session.id)}:${String(session.startMs)}:${String(session.status)}`;
  if (found !== null && seen(found) !== seen(lastOpen)) setLastOpen(found);
  const openSession = found ?? (lastOpen !== null && lastOpen.id === openId ? lastOpen : null);

  /** Page the grid to `day`, remembering which way it moved. */
  const pageTo = (day: CivilDay | null) => {
    const target = day ?? today;
    setGridMove(target < range.first ? 'calendarBack' : 'calendarForward');
    setChosenAnchor(day);
  };

  /** Choose a day; one outside the shown range pages the grid to it. */
  const goToDay = (day: CivilDay) => {
    setChosenDay(day);
    if (day < range.first || day > range.last) pageTo(day);
  };

  // Where the trainer is likely to go next is read before they go there: the neighbouring pages,
  // today's page and the other view of this one. A page they then open answers from the cache and
  // swaps in one quick fade instead of a skeleton — "Dziś" from a far week included.
  useEffect(() => {
    const neighbours = [
      visibleRange(view, stepAnchor(view, anchor, -1)),
      visibleRange(view, stepAnchor(view, anchor, 1)),
      visibleRange(view, today),
      visibleRange(view === 'week' ? 'month' : 'week', anchor),
    ];
    for (const entry of neighbours) {
      void queryClient.prefetchQuery(trainerScheduleQueryOptions(entry));
      void queryClient.prefetchQuery(trainerAvailabilityQueryOptions(entry));
    }
  }, [view, anchor, today, queryClient]);

  return {
    view,
    anchor,
    range,
    today,
    zone,
    // Said only once the hours have answered: before that the zone is a stand-in, not a claim.
    zoneNotice:
      availability.data === undefined ? null : zoneNotice(publishedZone, browserTimeZone()),
    // Read off the query's own state: the observer result does not carry the count. It changes only
    // when the schedule answers, which re-renders this hook through `schedule` anyway.
    readAnswers:
      queryClient.getQueryState(trainerScheduleQueryOptions(range).queryKey)?.dataUpdateCount ?? 0,
    policy: draftPolicy(policy.data),
    nowMs,
    gate,
    content,
    stale:
      content?.kind === 'ready' &&
      ((schedule.isError && schedule.data !== undefined) ||
        (availability.isError && availability.data !== undefined)),
    retry: () => {
      if (schedule.isError) void schedule.refetch();
      if (availability.isError) void availability.refetch();
    },
    selectedDay,
    awaiting: awaitingDecision(decisionSessions, nowMs),
    // Every schedule already cached — the neighbours are prefetched — so an empty week still knows.
    googleMeet: googleMeetSourceOf(google),
    callChoice: callLinkChoice(googleMeetSourceOf(google), standingCallLink, isApplePlatform(globalThis.navigator)),
    standingCallLink,
    callsOffered: backendOffersCalls([
      ...known,
      ...queryClient
        .getQueriesData<CalendarEventResponse[]>({ queryKey: [...BOOKING_ROOT, 'schedule'] })
        .flatMap(([, rows]) => rows ?? [])
        .map((event) => readBookingSession(event, zone)),
    ]),
    freeStarts,
    stepDay: (direction) => {
      goToDay(shiftDay(selectedDay, direction));
      setOpenId(null);
    },
    retryFreeStarts: () => {
      if (policy.isError) void policy.refetch();
      if (slots.isError) void slots.refetch();
    },
    openSession,
    gridMove,
    planShown,
    columnShown: planShown || openSession !== null,
    setPlanShown: (shown) => {
      setPlanShownState(shown);
      writeDayPlanShown(preferenceStore(), shown);
    },
    setView: (next) => {
      setGridMove('calendarView');
      setViewState(next);
      setChosenAnchor(selectedDay);
    },
    goToday: () => {
      pageTo(null);
      setChosenDay(today);
      setOpenId(null);
    },
    goPrevious: () => {
      setGridMove('calendarBack');
      setChosenAnchor(stepAnchor(view, anchor, -1));
      setOpenId(null);
    },
    goNext: () => {
      setGridMove('calendarForward');
      setChosenAnchor(stepAnchor(view, anchor, 1));
      setOpenId(null);
    },
    selectDay: (day) => {
      setChosenDay(day);
      setOpenId(null);
    },
    openSessionById: (id) => {
      const session = findSession(id);
      // Paging to its week when it is not on the grid, so the block is there and marked open.
      if (session?.day) goToDay(session.day);
      setOpenId(id);
    },
    showSession: (id, day) => {
      goToDay(day);
      setOpenId(id);
    },
    closeSession: () => {
      setOpenId(null);
    },
  };
}
